import * as Sentry from "@sentry/core";
import { useCallback, useState } from "react";
import { type ImageURISource, Image as NativeImage } from "react-native";
import { type GetProps, Image, isWeb, styled, View } from "tamagui";
import type { Status } from "../../shared-types/status";
import type { IconSizeToken } from "../../themes/token-size";
import { Icon, type IconComponent } from "../Icon";
import { Loader } from "../Loader";
import { SkeletonShimmer } from "../SkeletonShimmer";
import { Interface1, Interface3, Title5 } from "../Typography";
export type AvatarImageSource = string | number | ImageURISource;
const avatarStatusTokens = {
  neutral: {
    bg: "$surface0",
    text: "$textDefault",
    icon: "$iconDefault"
  },
  positive: {
    bg: "$bgPositive",
    text: "$textPositive",
    icon: "$iconPositive"
  },
  negative: {
    bg: "$bgNegative",
    text: "$textNegative",
    icon: "$iconNegative"
  },
  warning: {
    bg: "$bgWarning",
    text: "$textWarning",
    icon: "$iconWarning"
  },
  info: {
    bg: "$bgAccent",
    text: "$textAccent",
    icon: "$iconAccent"
  }
} as const satisfies Record<Status, {
  bg: string;
  text: string;
  icon: string;
}>;
const AvatarVariants = {
  size: {
    small: {
      height: "$si4",
      width: "$si4"
    },
    medium: {
      height: "$si6",
      width: "$si6"
    },
    large: {
      height: "$si8",
      width: "$si8"
    }
  },
  shape: {
    circle: {
      borderRadius: "$brFull"
    },
    square: {}
  },
  status: {
    neutral: {
      backgroundColor: avatarStatusTokens.neutral.bg
    },
    positive: {
      backgroundColor: avatarStatusTokens.positive.bg
    },
    negative: {
      backgroundColor: avatarStatusTokens.negative.bg
    },
    warning: {
      backgroundColor: avatarStatusTokens.warning.bg
    },
    info: {
      backgroundColor: avatarStatusTokens.info.bg
    }
  }
} as const;
export type AvatarSize = keyof (typeof AvatarVariants)["size"];
export type AvatarShape = keyof (typeof AvatarVariants)["shape"];
const StyledAvatar = styled(View, {
  name: "Avatar",
  variants: AvatarVariants,
  alignItems: "center",
  justifyContent: "center",
  defaultVariants: {
    shape: "circle",
    status: "neutral"
  }
});
const avatarSizeToIconSizeToken: Record<AvatarSize, IconSizeToken> = {
  large: "$iconXLarge",
  medium: "$iconLarge",
  small: "$iconSmall"
};
const squareBorderRadiusBySize = {
  small: "$br1",
  medium: "$br2",
  large: "$br2"
} as const;
type StyledAvatarProps = GetProps<typeof StyledAvatar>;
type BaseAvatarProps = StyledAvatarProps & {
  size?: AvatarSize;
  shape?: AvatarShape;
  status?: Status;
  objectFit?: "cover" | "contain";
  /** Replace the avatar content with a loading spinner. */
  loading?: boolean;
  /** Replace the avatar content with a skeleton shimmer (loading-state layout). */
  skeleton?: boolean;
};
type WithIcon = BaseAvatarProps & {
  icon: IconComponent;
  /** Initials max 2 characters - use getInitials to generate. */
  initials?: string;
  image?: AvatarImageSource;
};
type WithInitials = BaseAvatarProps & {
  icon?: IconComponent;
  /** Initials max 2 characters - use getInitials to generate. */
  initials: string;
  image?: AvatarImageSource;
};
type WithImage = BaseAvatarProps & {
  icon?: IconComponent;
  /** Initials max 2 characters - use getInitials to generate. */
  initials?: string;
  image: AvatarImageSource;
};

// `loading: true` discriminates this arm so callers can render a spinner-only
// Avatar (`<Avatar loading />`) without passing dummy content props.
type WithLoading = BaseAvatarProps & {
  loading: true;
  icon?: IconComponent;
  initials?: string;
  image?: AvatarImageSource;
};

// `skeleton: true` fills the size/shape-varianted frame with a shimmer for
// loading-state layouts; distinct from `loading` (spinner) semantics.
type WithSkeleton = BaseAvatarProps & {
  skeleton: true;
  icon?: IconComponent;
  initials?: string;
  image?: AvatarImageSource;
};

/** At least one of `icon`, `initials`, or `image` must be provided unless `loading` or `skeleton` is `true`. */
export type AvatarProps = WithLoading | WithSkeleton | WithIcon | WithInitials | WithImage;
const imageStyle = {
  width: "100%",
  height: "100%"
} as const;
const imageWebStyle = {
  ...imageStyle,
  mixBlendMode: "multiply"
} as const;
const AvatarTextLarge = styled(Title5, {
  name: "AvatarTextLarge"
});
const AvatarTextMedium = styled(Interface1, {
  name: "AvatarTextMedium"
});
const AvatarTextSmall = styled(Interface3, {
  name: "AvatarTextSmall"
});
const sizeToTextComponent = {
  large: AvatarTextLarge,
  medium: AvatarTextMedium,
  small: AvatarTextSmall
} as const;
export const Avatar = (props: AvatarProps) => {
  const {
    icon,
    initials,
    image,
    loading,
    skeleton,
    size = "medium",
    shape = "circle",
    status = "neutral",
    objectFit = "contain",
    ...rest
  } = props;
  const tokens = avatarStatusTokens[status];
  // Derive a stable key for the current image source so we can track load errors
  // per-image. When the image prop changes, the key changes and the previous
  // error state no longer matches, allowing a retry with the new source.
  const imageKey = typeof image === "string" ? image : typeof image === "number" ? image : image?.uri;
  const [errorImageKey, setErrorImageKey] = useState<string | number | undefined>(undefined);
  const imageError = image != null && imageKey == null || errorImageKey != null && errorImageKey === imageKey;
  const handleImageError = useCallback(() => {
    setErrorImageKey(imageKey);
    const imageSourceType = typeof image === "string" ? "string" : typeof image === "number" ? "number" : "uri-object";
    Sentry.captureException(new Error("Avatar image failed to load"), {
      level: "warning",
      extra: {
        imageSourceType
      }
    });
  }, [image, imageKey]);
  const nativeImageSource = image == null ? undefined : typeof image === "string" ? {
    uri: image
  } : image;
  const webImageSource = typeof image === "string" ? image : typeof image === "object" && image != null && "uri" in image ? image.uri : undefined;
  const TextComponent = sizeToTextComponent[size];
  return <StyledAvatar overflow="hidden" size={size} shape={shape} status={status} {...shape === "square" ? {
    borderRadius: squareBorderRadiusBySize[size]
  } : {}} {...rest} data-sentry-element="StyledAvatar" data-sentry-component="Avatar" data-sentry-source-file="Avatar.tsx">
      {/* If the image fails to load we gracefully fall back to initials, then icon, then empty. */}
      {skeleton ? <SkeletonShimmer radius={0} /> : loading ? <Loader size={avatarSizeToIconSizeToken[size]} color={tokens.icon} /> : image && !imageError && isWeb && webImageSource != null ? <Image src={webImageSource} onError={handleImageError} style={imageWebStyle} objectFit={objectFit} /> : image && !imageError && !isWeb && nativeImageSource != null ? <NativeImage source={nativeImageSource} onError={handleImageError} style={imageStyle} resizeMode={objectFit} /> : initials ? <TextComponent color={tokens.text}>{initials}</TextComponent> : icon ? <Icon icon={icon} size={avatarSizeToIconSizeToken[size]} color={tokens.icon} /> : null}
    </StyledAvatar>;
};